import React, { useState, useEffect } from "react";
import { RiArrowRightSLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { RootState } from "../store";
import { useSelector } from "react-redux";
import { ageOptions, heightOptions } from "../utils/constants";
import { getRequest } from "../helpers/api.helper";

type Option = { value: string; label: string };

// Reusable Modal Component
const SelectionModal = ({
  isOpen,
  onClose,
  title,
  options,
  selectedValues,
  onCheckboxChange,
  onSelectAllChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  options: Option[];
  selectedValues: string[];
  onCheckboxChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    option: { value: string; label: string }
  ) => void;
  onSelectAllChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredOptions = options?.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isAllSelected = selectedValues?.length === options?.length;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 animate-fadeIn"
      onClick={onClose}
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-3xl w-full max-w-md max-h-[calc(100vh-2rem)] sm:max-h-[85vh] overflow-hidden flex flex-col shadow-2xl animate-slideUp transform"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 capitalize">
              {title.replace(/_/g, " ")}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-white/50 rounded-full"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white shadow-sm"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Options List */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4">
          {/* Select All Option */}
          <label className="flex items-center space-x-3 p-3 mb-2 cursor-pointer hover:bg-blue-50 rounded-xl transition-all border-2 border-transparent hover:border-blue-100">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={onSelectAllChange}
              className="w-5 h-5 text-blue-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 cursor-pointer"
            />
            <span className="font-semibold text-gray-800 text-sm sm:text-base">
              Select All ({options?.length || 0})
            </span>
          </label>

          {/* Divider */}
          <div className="border-t border-gray-200 my-3"></div>

          {/* Individual Options */}
          <div className="space-y-1">
            {filteredOptions?.length > 0 ? (
              filteredOptions.map((option: any) => (
                <label
                  key={option.value}
                  className="flex items-center space-x-3 p-3 cursor-pointer hover:bg-gray-50 rounded-xl transition-all group"
                >
                  <input
                    type="checkbox"
                    checked={selectedValues?.includes(option.value)}
                    onChange={(e) => onCheckboxChange(e, option)}
                    className="w-5 h-5 text-blue-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 cursor-pointer"
                  />
                  <span className="text-gray-700 group-hover:text-gray-900 text-sm sm:text-base">
                    {option.label}
                  </span>
                </label>
              ))
            ) : (
              <div className="text-center py-8 text-gray-400">
                <svg
                  className="w-12 h-12 mx-auto mb-2 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm">No options found</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 sm:p-6 border-t border-gray-100 bg-gray-50 pb-[calc(var(--bottom-safe-area)+1.5rem)]">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
            >
              Cancel
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all shadow-md"
            >
              Done (
              {selectedValues?.filter((v: any) => v !== "Any").length || 0})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdvancedSearch = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.bureau.data);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    category: number | null;
    key:
      | "Religion_Preferences"
      | "Caste_Preferences"
      | "Sub_Caste_Preferences"
      | "Mother_Toungue_Preferences"
      | "Marital_Status_Preferences"
      | "children_PreferenceOptions"
      | "Created_by_preference"
      | "Physical_status_preference"
      | "Eating_habits_preference"
      | "Education_Preferences"
      | "Occupation_Preference"
      | "Job_Location_Preference"
      | "Anuual_Income_Preference"
      | "Partner_Family_Preference"
      | "Setelled_Location_Preference"
      | null;
    options: Option[];
  }>({
    isOpen: false,
    category: null,
    key: null,
    options: [],
  });
  const [loading, setLoading] = useState(false);

  const [city, setCity] = useState([]);
  const [education, setEducation] = useState([]);
  const [annualIncome, setAnnualIncome] = useState([]);
  const [occupation, setOccupation] = useState([]);
  const [country, setCountry] = useState([]);
  const [state, setState] = useState([]);
  const [caste, setCaste] = useState([]);
  const [subcaste, setSubcaste] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const response = await getRequest(`/common/all`);
    };
    fetchData();
  });

  const [selectedOptions, setSelectedOptions] = useState({
    Religion_Preferences: ["Any"],
    Caste_Preferences: ["Any"],
    Sub_Caste_Preferences: ["Any"],
    Mother_Toungue_Preferences: ["Any"],
    Marital_Status_Preferences: ["Any"],
    children_PreferenceOptions: ["Any"],
    Created_by_preference: ["Any"],
    Physical_status_preference: ["Any"],
    Eating_habits_preference: ["Any"],
  });

  const [selectedOptions2, setSelectedOptions2] = useState({
    Education_Preferences: ["Any"],
    Occupation_Preference: ["Any"],
    Job_Location_Preference: ["Any"],
    Anuual_Income_Preference: ["Any"],
    Partner_Family_Preference: ["Any"],
    Setelled_Location_Preference: ["Any"],
  });

  const [selectedOptions3, setSelectedOptions3] = useState({
    Country_Preference: ["Any"],
    State_Preference: ["Any"],
    City_Preference: ["Any"],
    Citizenship_Preference: ["Any"],
  });

  const dropdownOptions4 = {
    Service_Preference: [
      { value: "Any", label: "Any" },
      {
        value: "Only online service profiles",
        label: "Only online service profiles",
      },
      {
        value: "Only offline services profiles",
        label: "Only offline services profiles",
      },
      { value: "Online/offline profiles", label: "Online/offline profiles" },
    ],
  };
  const [selectedOptions4, setSelectedOptions4] = useState({
    Service_Preference: ["Any"],
  });

  const [ageRange, setAgeRange] = useState({ min: 18, max: 60 });
  const [heightRange, setHeightRange] = useState({ min: 4.0, max: 6.5 });
  const [selectedGender, setSelectedGender] = useState("");

  const openModal = (category: any, key: any, options: any) => {
    setModalState({
      isOpen: true,
      category,
      key,
      options,
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      category: null,
      key: null,
      options: [],
    });
  };

  const dropdownOptions = {
    Religion_Preferences: [
      { value: "hindu", label: "Hindu" },
      { value: "muslim", label: "Muslim" },
      { value: "christian", label: "Christian" },
      { value: "sikh", label: "Sikh" },
      { value: "buddhist", label: "Buddhist" },
      { value: "jain", label: "Jain" },
      { value: "parsi", label: "Parsi" },
      { value: "other", label: "Other" },
    ],
    Caste_Preferences: [
      ...caste.map((caste: any) => ({
        value: caste.caste,
        label: caste.caste,
      })),
    ],
    Sub_Caste_Preferences: [
      ...subcaste.map((caste: any) => ({
        value: caste.sub_caste,
        label: caste.sub_caste,
      })),
    ],
    Mother_Toungue_Preferences: [
      { value: "Hindi", label: "Hindi" },
      { value: "English", label: "English" },
      { value: "Tamil", label: "Tamil" },
      { value: "Telugu", label: "Telugu" },
      { value: "Kannada", label: "Kannada" },
      { value: "Malayalam", label: "Malayalam" },
      { value: "Bengali", label: "Bengali" },
      { value: "Marathi", label: "Marathi" },
      { value: "Gujarati", label: "Gujarati" },
      { value: "Punjabi", label: "Punjabi" },
      { value: "Other", label: "Other" },
    ],
    Marital_Status_Preferences: [
      { value: "neverMarried", label: "Never Married" },
      { value: "awaitingDivorce", label: "Awaiting Divorce" },
      { value: "divorced", label: "Divorced" },
      { value: "widow", label: "Widowed" },
    ],
    children_PreferenceOptions: [
      { value: "no_children", label: "No children" },
      { value: "want_children", label: "Want children" },
      { value: "has_children", label: "Has children" },
      { value: "not_sure_yet", label: "Not sure yet" },
    ],
    Created_by_preference: [
      { value: "self", label: "Self" },
      { value: "father", label: "Father" },
      { value: "mother", label: "Mother" },
      { value: "friend", label: "Friend" },
      { value: "relatives", label: "Relatives" },
      { value: "sister", label: "Sister" },
      { value: "brother", label: "Brother" },
    ],
    Physical_status_preference: [
      { value: "normal", label: "Normal" },
      { value: "physically_challenged", label: "Physically Challenged" },
    ],
    Eating_habits_preference: [
      { value: "vegetarian", label: "Vegetarian" },
      { value: "non_vegetarian", label: "Non-Vegetarian" },
      { value: "eggetarian", label: "Eggetarian" },
      { value: "jain", label: "Jain" },
    ],
  };

  const dropdownOptions2 = {
    Education_Preferences: [
      ...education.map((education: any) => ({
        value: education.education,
        label: education.education,
      })),
    ],
    Occupation_Preference: [
      ...occupation.map((occupation: any) => ({
        value: occupation.occupation,
        label: occupation.occupation,
      })),
    ],
    Job_Location_Preference: [
      ...city.map((job_location: any) => ({
        value: job_location.city,
        label: job_location.city,
      })),
    ],
    Anuual_Income_Preference: [
      ...annualIncome.map((annual_income: any) => ({
        value: annual_income.annual_income,
        label: annual_income.annual_income,
      })),
    ],
    Partner_Family_Preference: [
      { value: "Joint_Family", label: "Joint Family" },
      { value: "Nuclear_Family", label: "Nuclear Family" },
      { value: "Family-Oriented_Partner", label: "Family-Oriented Partner" },
      { value: "Independent_Partner", label: "Independent Partner" },
      { value: "Living_with_Parents", label: "Living with Parents" },
    ],
    Setelled_Location_Preference: [
      ...city.map((job_location: any) => ({
        value: job_location.city,
        label: job_location.city,
      })),
    ],
  };

  const dropdownOptions3 = {
    Country_Preference: [
      ...country.map((job_location: any) => ({
        value: job_location.country,
        label: job_location.country,
      })),
    ],
    State_Preference: [
      ...state.map((job_location: any) => ({
        value: job_location.state,
        label: job_location.state,
      })),
    ],
    City_Preference: [
      ...city.map((job_location: any) => ({
        value: job_location.city,
        label: job_location.city,
      })),
    ],
    Citizenship_Preference: [
      { value: "Indian", label: "Indian" },
      { value: "NRI", label: "NRI" },
      { value: "OCI", label: "OCI" },
      { value: "Other", label: "Other" },
    ],
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRequest(`/common/all`);
        if (response.success && response.data) {
          const data = response.data;

          // Utility: Remove duplicates based on a specific key
          const uniqueByKey = <T extends Record<string, any>>(
            arr: T[],
            key: keyof T
          ): T[] => {
            const seen = new Set();
            return arr.filter((item) => {
              const val = item[key];
              if (!val || seen.has(val)) return false;
              seen.add(val);
              return true;
            });
          };

          setCity(uniqueByKey(data.city || [], "city"));
          setState(uniqueByKey(data.state || [], "state"));
          setEducation(uniqueByKey(data.education || [], "education"));
          setAnnualIncome(
            uniqueByKey(data.annualIncome || [], "annual_income")
          );
          setOccupation(uniqueByKey(data.occupation || [], "occupation"));
          setCountry(uniqueByKey(data.country || [], "country"));
          setCaste(uniqueByKey(data.caste || [], "caste"));
          setSubcaste(uniqueByKey(data.subCaste || [], "sub_caste"));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleCheckboxChange = (event: any, option: any) => {
    const { checked } = event.target;
    const { category, key } = modalState;

    let currentOptions, setterFunction;

    if (category === 1) {
      //@ts-ignore
      currentOptions = selectedOptions[key] || [];
      setterFunction = setSelectedOptions;
    } else if (category === 2) {
      //@ts-ignore
      currentOptions = selectedOptions2[key] || [];
      setterFunction = setSelectedOptions2;
    } else if (category === 3) {
      //@ts-ignore
      currentOptions = selectedOptions3[key] || [];
      setterFunction = setSelectedOptions3;
    } else if (category === 4) {
      //@ts-ignore
      currentOptions = selectedOptions4[key] || [];
      setterFunction = setSelectedOptions4;
    }

    if (checked) {
      const updatedSelectedOptions = currentOptions
        .filter((value: any) => value !== "Any")
        .concat(option.value);
      setterFunction?.((prev: any) => ({
        ...prev,
        [key as any]: updatedSelectedOptions,
      }));
    } else {
      const updatedSelectedOptions = currentOptions.filter(
        (value: any) => value !== option.value
      );
      if (updatedSelectedOptions.length === 0) {
        setterFunction?.((prev: any) => ({ ...prev, [key as any]: ["Any"] }));
      } else {
        setterFunction?.((prev: any) => ({
          ...prev,
          [key as any]: updatedSelectedOptions,
        }));
      }
    }
  };

  const handleSelectAllChange = (event: any) => {
    const { checked } = event.target;
    const { category, key, options } = modalState;

    let setterFunction;

    if (category === 1) {
      setterFunction = setSelectedOptions;
    } else if (category === 2) {
      setterFunction = setSelectedOptions2;
    } else if (category === 3) {
      setterFunction = setSelectedOptions3;
    } else if (category === 4) {
      setterFunction = setSelectedOptions4;
    }

    if (checked) {
      setterFunction?.((prev: any) => ({
        ...prev,
        [key as any]: options.map((option: any) => option.value),
      }));
    } else {
      setterFunction?.((prev: any) => ({
        ...prev,
        [key as any]: ["Any"],
      }));
    }
  };

  const getSelectedValues = () => {
    const { category, key } = modalState;
    if (!key) return [];

    if (category === 1 && key in selectedOptions) {
      return selectedOptions[key as keyof typeof selectedOptions];
    }
    if (category === 2 && key in selectedOptions2) {
      return selectedOptions2[key as keyof typeof selectedOptions2];
    }
    if (category === 3 && key in selectedOptions3) {
      return selectedOptions3[key as keyof typeof selectedOptions3];
    }
    if (category === 4 && key in selectedOptions4) {
      return selectedOptions4[key as keyof typeof selectedOptions4];
    }
    return [];
  };

  const processPreferences = (preferences: any) => {
    if (
      !preferences ||
      preferences.length === 0 ||
      preferences.includes("Any")
    ) {
      return "Any";
    }
    return preferences.join(", ");
  };

  const getDisplayText = (key: any, options: any, selectedValues: any) => {
    if (
      Array.isArray(selectedValues) &&
      Array.isArray(options) &&
      selectedValues?.length === options?.length
    ) {
      return "Any";
    }

    if (
      selectedValues?.length > 0 &&
      !selectedValues?.every((item: any) => item === "Any")
    ) {
      return options
        ?.filter((opt: any) => selectedValues?.includes(opt.value))
        .map((opt: any) => opt.label)
        .join(", ");
    }

    return "Any";
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!user) {
      alert("Please wait while we load your profile data.");
      return;
    }

    const searchData = {
      gender: selectedGender,
      ageMin: ageRange.min,
      ageMax: ageRange.max,
      heightMin: heightRange.min,
      heightMax: heightRange.max,
      religionPreferences: processPreferences(
        selectedOptions.Religion_Preferences
      ),
      castePreferences: processPreferences(selectedOptions.Caste_Preferences),
      subCastePreferences: processPreferences(
        selectedOptions.Sub_Caste_Preferences
      ),
      motherTonguePreferences: processPreferences(
        selectedOptions.Mother_Toungue_Preferences
      ),
      maritalStatusPreferences: processPreferences(
        selectedOptions.Marital_Status_Preferences
      ),
      childrenPreferences: processPreferences(
        selectedOptions.children_PreferenceOptions
      ),
      createdByPreferences: processPreferences(
        selectedOptions.Created_by_preference
      ),
      physicalStatusPreferences: processPreferences(
        selectedOptions.Physical_status_preference
      ),
      eatingHabitsPreferences: processPreferences(
        selectedOptions.Eating_habits_preference
      ),
      educationPreferences: processPreferences(
        selectedOptions2.Education_Preferences
      ),
      occupationPreferences: processPreferences(
        selectedOptions2.Occupation_Preference
      ),
      jobLocationPreferences: processPreferences(
        selectedOptions2.Job_Location_Preference
      ),
      annualIncomePreferences: processPreferences(
        selectedOptions2.Anuual_Income_Preference
      ),
      familyPreferences: processPreferences(
        selectedOptions2.Partner_Family_Preference
      ),
      settledLocationPreferences: processPreferences(
        selectedOptions2.Setelled_Location_Preference
      ),
      countryPreferences: processPreferences(
        selectedOptions3.Country_Preference
      ),
      statePreferences: processPreferences(selectedOptions3.State_Preference),
      cityPreferences: processPreferences(selectedOptions3.City_Preference),
      citizenshipPreferences: processPreferences(
        selectedOptions3.Citizenship_Preference
      ),
      servicePreferences: processPreferences(
        selectedOptions4.Service_Preference
      ),
    };
    try {
      navigate("/admin/advanced-search-results", { state: { searchData } });
    } catch (error) {
      console.error("Error processing search:", error);
      alert("An error occurred while processing your search.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 pb-24">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>

      <div className="">
        {/* Main Form */}
        <div className="bg-white rounded-2xl shadow-lg p-2 sm:p-4 md:p-10 space-y-10">
          <form onSubmit={handleSubmit} className="space-y-10">
            <section className="shadow-2xl bg-white px-2 py-4 rounded">
              <div className="text-center pb-3">
                <button
                  type="button"
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white p-3 rounded-lg hover:from-orange-600 hover:to-red-600 transition-all font-semibold shadow-md"
                >
                  * Select Gender to Search For *
                </button>
              </div>

              <div className="mb-[20px] rounded-lg">
                <label className="block text-red-500 font-medium mb-2">
                  Gender
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setSelectedGender("male")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedGender === "male"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">👨</div>
                      <div className="font-semibold">Male</div>
                      <div className="text-sm text-gray-600">
                        Search for Male profiles
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedGender("female")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedGender === "female"
                        ? "border-pink-500 bg-pink-50 text-pink-700"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">👩</div>
                      <div className="font-semibold">Female</div>
                      <div className="text-sm text-gray-600">
                        Search for Female profiles
                      </div>
                    </div>
                  </button>
                </div>

                {selectedGender && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-700 text-center font-medium">
                      Searching for{" "}
                      {selectedGender === "male" ? "Male" : "Female"} profiles
                    </p>
                  </div>
                )}
              </div>
            </section>
            {/* Age + Height Range */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Age Range */}
              <section className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 p-4">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                      Age Range
                    </h2>
                    <p className="text-sm text-gray-600">
                      Select your preferred age range
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      From
                    </label>
                    <div className="relative">
                      <select
                        value={ageRange.min}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (value >= 18 && value <= ageRange.max) {
                            setAgeRange({ ...ageRange, min: value });
                          }
                        }}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 appearance-none bg-white"
                      >
                        {ageOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      To
                    </label>
                    <div className="relative">
                      <select
                        value={ageRange.max}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (value >= ageRange.min && value <= 70) {
                            setAgeRange({ ...ageRange, max: value });
                          }
                        }}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 appearance-none bg-white"
                      >
                        {ageOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-500 mt-4">
                  Selected:{" "}
                  <span className="font-semibold text-blue-600">
                    {ageRange.min}
                  </span>{" "}
                  –{" "}
                  <span className="font-semibold text-indigo-600">
                    {ageRange.max}
                  </span>{" "}
                  years
                </p>
              </section>

              {/* Height Range */}
              <section className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                      Height Range
                    </h2>
                    <p className="text-sm text-gray-600">
                      Select your preferred height range
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      From
                    </label>

                    <div className="relative">
                      <select
                        value={heightRange.min}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (value >= 4.0 && value <= heightRange.max) {
                            setHeightRange({ ...heightRange, min: value });
                          }
                        }}
                        className="w-full px-3 appearance-none py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        {heightOptions.map((option) => (
                          <option key={option.label} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      To
                    </label>

                    <div className="relative">
                      <select
                        value={heightRange.max}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (value >= heightRange.min && value <= 7.0) {
                            setHeightRange({ ...heightRange, max: value });
                          }
                        }}
                        className="w-full px-3 py-2 border appearance-none border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        {heightOptions.map((option) => (
                          <option key={option.label} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-500 mt-4">
                  Selected:{" "}
                  <span className="font-semibold text-green-600">
                    {heightRange.min.toFixed(1)}
                  </span>{" "}
                  –{" "}
                  <span className="font-semibold text-emerald-600">
                    {heightRange.max.toFixed(1)}
                  </span>{" "}
                  ft
                </p>
              </section>
            </div>
            {/* Basic & Professional Preferences */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Basic Preferences */}
              <section className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">
                  Basic Preferences
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {(
                    Object.keys(dropdownOptions) as Array<
                      keyof typeof dropdownOptions
                    >
                  ).map((key) => (
                    <div key={key}>
                      <label className="block text-red-500 font-medium mb-2 capitalize">
                        {key.replace(/_/g, " ")}
                      </label>
                      <div
                        className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-md transition-all"
                        onClick={() => openModal(1, key, dropdownOptions[key])}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm font-medium truncate">
                            {getDisplayText(
                              key,
                              dropdownOptions[key],
                              selectedOptions[
                                key as keyof typeof selectedOptions
                              ]
                            )}
                          </span>
                          <RiArrowRightSLine className="text-2xl text-gray-500 ml-2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Professional Preferences */}
              <section className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">
                  Professional Preferences
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {(
                    Object.keys(dropdownOptions2) as Array<
                      keyof typeof dropdownOptions2
                    >
                  ).map((key) => (
                    <div key={key}>
                      <label className="block text-red-500 font-medium mb-2 capitalize">
                        {key.replace(/_/g, " ")}
                      </label>
                      <div
                        className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-md transition-all"
                        onClick={() => openModal(2, key, dropdownOptions2[key])}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm font-medium truncate">
                            {getDisplayText(
                              key,
                              dropdownOptions2[key],
                              selectedOptions2[
                                key as keyof typeof selectedOptions2
                              ]
                            )}
                          </span>
                          <RiArrowRightSLine className="text-2xl text-gray-500 ml-2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Location & Service Preferences */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Location Preferences */}
              <section className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">
                  Location Preferences
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {(
                    Object.keys(dropdownOptions3) as Array<
                      keyof typeof dropdownOptions3
                    >
                  ).map((key) => (
                    <div key={key}>
                      <label className="block text-red-500 font-medium mb-2 capitalize">
                        {key.replace(/_/g, " ")}
                      </label>
                      <div
                        className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-md transition-all"
                        onClick={() => openModal(3, key, dropdownOptions3[key])}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm font-medium truncate">
                            {getDisplayText(
                              key,
                              dropdownOptions3[key],
                              selectedOptions3[key]
                            )}
                          </span>
                          <RiArrowRightSLine className="text-2xl text-gray-500 ml-2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Service Preferences */}
              <section className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">
                  Service Preferences
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-red-500 font-medium mb-2 capitalize">
                      Service Preference
                    </label>
                    <div
                      className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-md transition-all"
                      onClick={() =>
                        openModal(
                          4,
                          "Service_Preference",
                          dropdownOptions4.Service_Preference
                        )
                      }
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-sm font-medium truncate">
                          {getDisplayText(
                            "Service_Preference",
                            dropdownOptions4.Service_Preference,
                            selectedOptions4.Service_Preference
                          )}
                        </span>
                        <RiArrowRightSLine className="text-2xl text-gray-500 ml-2" />
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                className="w-full sm:w-1/2 lg:w-1/3 mx-auto bg-gradient-to-r from-red-500 to-pink-600 text-white py-3 sm:py-4 rounded-xl text-base sm:text-lg font-semibold shadow-md hover:shadow-lg transition-all"
              >
                Search Profiles
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Reusable Modal */}
      <SelectionModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.key || ""}
        options={modalState.options || []}
        selectedValues={getSelectedValues()}
        onCheckboxChange={handleCheckboxChange}
        onSelectAllChange={handleSelectAllChange}
      />
    </div>
  );
};
export default AdvancedSearch;
